import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Stack, TextField, Alert, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  CircularProgress, Grid, Divider, Tooltip, IconButton, Snackbar, Dialog,
  DialogTitle, DialogContent, DialogActions, InputAdornment, Accordion,
  AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  Fingerprint, Key, Refresh, ContentCopy, CheckCircle, Devices, SystemUpdateAlt,
  PlayArrow, HelpOutline, Person, Search, Edit, Save, ExpandMore, VpnKey, Send
} from '@mui/icons-material';

import { usersAPI } from '../services/api';
import axios from 'axios';
import PageHeader from '../components/PageHeader';
import { audioService } from '../utils/audioService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const WEBHOOK_BASE_URL = process.env.REACT_APP_WEBHOOK_BASE_URL || API_URL.replace(/\/api$/, '');
const BASE_SERVER_URL = WEBHOOK_BASE_URL;

const getServerInfo = () => {
  try {
    const url = new URL(WEBHOOK_BASE_URL);
    const isHttps = url.protocol === 'https:';
    const host = url.hostname;
    const defaultPort = isHttps ? '443' : '80';
    const port = url.port || defaultPort;
    const isDomain = !/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(host) && host !== 'localhost';
    return {
      host,
      port,
      isHttps,
      isDomain,
      enableDomainName: isDomain ? 'ON' : 'OFF',
    };
  } catch {
    return {
      host: 'localhost',
      port: '5000',
      isHttps: false,
      isDomain: false,
      enableDomainName: 'OFF',
    };
  }
};

const CodeTag = ({ children }) => (
  <Box
    component="span"
    sx={{
      fontFamily: 'monospace',
      bgcolor: '#F3F4F6',
      color: '#1F2937',
      px: 1,
      py: 0.3,
      borderRadius: 1,
      fontSize: '0.85em',
      fontWeight: 700,
      border: '1px solid #E5E7EB',
      display: 'inline-block',
      my: 0.2,
    }}
  >
    {children}
  </Box>
);

export default function BiometricIntegration() {
  const [loadingKey, setLoadingKey] = useState(false);
  const [integrationKey, setIntegrationKey] = useState('');
  
  // Member EmpCode mapping state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUserId, setEditingUserId] = useState(null);
  const [editEmpCode, setEditEmpCode] = useState('');

  // Simulator State
  const [simEmpCode, setSimEmpCode] = useState('');
  const [simulating, setSimulating] = useState(false);
  const [simResult, setSimResult] = useState(null);

  // Snackbar Notification
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchIntegrationKey();
    fetchUsers();
  }, []);

  const fetchIntegrationKey = async () => {
    setLoadingKey(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/integrations/key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setIntegrationKey(res.data.key);
      }
    } catch (err) {
      console.error('Failed to fetch integration key:', err);
    } finally {
      setLoadingKey(false);
    }
  };

  const handleRegenerateKey = async () => {
    if (!window.confirm('Are you sure you want to regenerate your integration key? Existing devices will need to be updated.')) return;
    setLoadingKey(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/integrations/key/regenerate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setIntegrationKey(res.data.key);
        setSnackbar({ open: true, message: 'Integration Key regenerated successfully!', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to regenerate integration key', severity: 'error' });
    } finally {
      setLoadingKey(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await usersAPI.getAll({ limit: 200 });
      if (res.data.success) {
        setUsers(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSaveEmpCode = async (userId) => {
    try {
      const res = await usersAPI.update(userId, { esslEmpCode: editEmpCode.trim() || null });
      if (res.data.success) {
        setUsers((prev) =>
          prev.map((u) => (u._id === userId ? { ...u, esslEmpCode: editEmpCode.trim() || null } : u))
        );
        setSnackbar({ open: true, message: 'Member Biometric EmpCode updated!', severity: 'success' });
        setEditingUserId(null);
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update member EmpCode', severity: 'error' });
    }
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: `${label} copied to clipboard!`, severity: 'info' });
  };

  const handleSimulateCheckIn = async () => {
    if (!simEmpCode.trim()) {
      setSnackbar({ open: true, message: 'Please enter a Biometric EmpCode to test', severity: 'warning' });
      return;
    }

    setSimulating(true);
    setSimResult(null);

    try {
      const res = await axios.post(`${API_URL}/integrations/essl/webhook`, {
        empCode: simEmpCode.trim(),
        timestamp: new Date().toISOString()
      }, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': integrationKey
        }
      });

      audioService.playAttendanceSuccessSound();
      setSimResult({
        success: true,
        message: res.data.message,
        userName: res.data.data?.userName || 'Member',
        checkInTime: res.data.data?.checkInTime
      });
      setSnackbar({ open: true, message: 'Biometric scan simulated successfully!', severity: 'success' });
    } catch (err) {
      audioService.playErrorSound();
      setSimResult({
        success: false,
        message: err.response?.data?.message || 'Biometric check-in failed'
      });
      setSnackbar({ open: true, message: err.response?.data?.message || 'Check-in test failed', severity: 'error' });
    } finally {
      setSimulating(false);
    }
  };

  const filteredUsers = users.filter((u) =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.esslEmpCode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.batchName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const webhookUrl = `${API_URL}/integrations/essl/webhook`;
  const admsUrl = `${BASE_SERVER_URL}/iclock/cdata`;

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="eSSL Biometric & Hardware Integration"
        subtitle="Connect eSSL Fingerprint devices (K30, X990, iFace, ADMS) and eTimeTrackLite software to record automatic gym attendance."
        gradient="linear-gradient(90deg, #0891B2, #22D3EE)"
        actions={<Button variant="outlined" size="small" startIcon={<Refresh />} onClick={() => { fetchIntegrationKey(); fetchUsers(); }}>Refresh</Button>}
      />

      {/* Top Credentials & Configuration Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Gym Integration API Key */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', bgcolor: '#f8fafc' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                <VpnKey sx={{ color: 'primary.main', fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700}>
                  Gym Secret Integration Key
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Include this secret key in the <CodeTag>x-api-key</CodeTag> HTTP header when sending biometric webhooks.
              </Typography>

              {loadingKey ? (
                <CircularProgress size={24} />
              ) : (
                <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', bgcolor: '#fff', borderRadius: 2 }}>
                  <Typography variant="body2" fontFamily="monospace" fontWeight={700} sx={{ wordBreak: 'break-all', flexGrow: 1 }}>
                    {integrationKey || 'No Key Generated'}
                  </Typography>
                  <Tooltip title="Copy Key">
                    <IconButton size="small" onClick={() => handleCopy(integrationKey, 'Integration Key')}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Paper>
              )}

              <Stack direction="row" spacing={1} mt={2}>
                <Button size="small" variant="outlined" color="warning" onClick={handleRegenerateKey} disabled={loadingKey}>
                  Regenerate Secret Key
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Webhook & ADMS Endpoints */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', bgcolor: '#f0fdf4', borderColor: '#bbf7d0' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                <Devices sx={{ color: 'success.main', fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700} color="success.main">
                  Cloud Server Webhook & ADMS Endpoints
                </Typography>
              </Stack>
              
              {/* REST Webhook */}
              <Box mb={2}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.5}>
                  REST Webhook Endpoint (For eTimeTrackLite / PC Software):
                </Typography>
                <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', bgcolor: '#fff', borderRadius: 1.5 }}>
                  <Typography variant="caption" fontFamily="monospace" fontWeight={600} flexGrow={1} noWrap>
                    {webhookUrl}
                  </Typography>
                  <Tooltip title="Copy Webhook URL">
                    <IconButton size="small" onClick={() => handleCopy(webhookUrl, 'Webhook URL')}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Paper>
              </Box>

              {/* ADMS Cloud Push */}
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.5}>
                  eSSL ADMS Hardware Server URL (For direct K30/X990 Wi-Fi Cloud Push):
                </Typography>
                <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', bgcolor: '#fff', borderRadius: 1.5 }}>
                  <Typography variant="caption" fontFamily="monospace" fontWeight={600} flexGrow={1} noWrap>
                    {admsUrl}
                  </Typography>
                  <Tooltip title="Copy ADMS Server URL">
                    <IconButton size="small" onClick={() => handleCopy(admsUrl, 'ADMS Server URL')}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Paper>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Interactive Biometric Check-in Simulator */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
            <Fingerprint sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700}>
              Live Biometric Check-in Test Simulator
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Test your biometric check-in logic by simulating a fingerprint scan with a member's assigned <CodeTag>empCode</CodeTag>.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <TextField
              size="small"
              label="Biometric EmpCode"
              placeholder="e.g. 101"
              value={simEmpCode}
              onChange={(e) => setSimEmpCode(e.target.value)}
              sx={{ width: { xs: '100%', sm: 280 } }}
            />
            <Button
              variant="contained"
              startIcon={simulating ? <CircularProgress size={18} color="inherit" /> : <Send />}
              onClick={handleSimulateCheckIn}
              disabled={simulating}
              sx={{ height: 40, px: 2.5, fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              Simulate Fingerprint Scan
            </Button>
          </Stack>

          {simResult && (
            <Alert
              severity={simResult.success ? 'success' : 'error'}
              sx={{ mt: 2, borderRadius: 2 }}
              icon={simResult.success ? <CheckCircle /> : undefined}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                {simResult.message}
              </Typography>
              {simResult.success && (
                <Typography variant="caption" display="block">
                  Member: <strong>{simResult.userName}</strong> • Check-in Time: {new Date(simResult.checkInTime).toLocaleTimeString()}
                </Typography>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Member EmpCode Mapping Table */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} mb={2}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Member Biometric EmpCode Mapping
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Assign unique eSSL Device IDs (<CodeTag>empCode</CodeTag>) to members for automatic check-ins.
              </Typography>
            </Box>

            <TextField
              size="small"
              placeholder="Search by name, phone, or empCode..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 300 } }}
            />
          </Stack>

          {loadingUsers ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Member Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Role / Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Batch</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Biometric EmpCode</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        No members found matching search query.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user._id} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Person sx={{ color: 'text.secondary', fontSize: 20 }} />
                            <Typography variant="body2" fontWeight={600}>
                              {user.name}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <Chip
                              label={user.role || 'user'}
                              size="small"
                              sx={{
                                textTransform: 'capitalize',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                bgcolor: '#F3F4F6',
                                color: '#374151',
                                border: '1px solid #E5E7EB',
                              }}
                            />
                            <Chip
                              label={user.membershipStatus || 'active'}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                textTransform: 'capitalize',
                                ...(user.membershipStatus === 'inactive' || user.membershipStatus === 'expired'
                                  ? { bgcolor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }
                                  : { bgcolor: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0' }
                                )
                              }}
                            />
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {user.batchName ? (
                            <Chip
                              label={user.batchName}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                bgcolor: '#FAF5FF',
                                color: '#7E22CE',
                                border: '1px solid #E9D5FF',
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {user.phone || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {editingUserId === user._id ? (
                            <TextField
                              size="small"
                              value={editEmpCode}
                              onChange={(e) => setEditEmpCode(e.target.value)}
                              placeholder="e.g. 101"
                              sx={{ width: 120 }}
                              autoFocus
                            />
                          ) : user.esslEmpCode ? (
                            <Chip
                              icon={<Fingerprint style={{ fontSize: 14, color: '#4F46E5' }} />}
                              label={user.esslEmpCode}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                bgcolor: '#EEF2FF',
                                color: '#4F46E5',
                                border: '1px solid #C7D2FE',
                                '& .MuiChip-icon': { color: '#4F46E5' },
                              }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary" fontStyle="italic">
                              Not assigned
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {editingUserId === user._id ? (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button size="small" variant="contained" color="success" onClick={() => handleSaveEmpCode(user._id)}>
                                Save
                              </Button>
                              <Button size="small" variant="outlined" onClick={() => setEditingUserId(null)}>
                                Cancel
                              </Button>
                            </Stack>
                          ) : (
                            <Button
                              size="small"
                              startIcon={<Edit />}
                              onClick={() => {
                                setEditingUserId(user._id);
                                setEditEmpCode(user.esslEmpCode || '');
                              }}
                            >
                              Edit ID
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Hardware Setup Guide */}
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
            <HelpOutline sx={{ color: 'info.main', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700}>
              Hardware & Software Integration Setup Instructions
            </Typography>
          </Stack>

          <Accordion defaultExpanded variant="outlined" sx={{ mb: 1, borderRadius: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight={700}>Method 1: Direct eSSL Hardware Cloud ADMS Setup (K30, X990, iFace)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {(() => {
                const info = getServerInfo();
                return (
                  <>
                    <Typography variant="body2" paragraph>
                      1. On your eSSL device physical keypad, open <strong>Menu -&gt; Comm. -&gt; Cloud Server Settings / ADMS</strong>.
                    </Typography>
                    <Typography variant="body2" paragraph>
                      2. Set <strong>Enable Domain Name</strong> to <CodeTag>{info.enableDomainName}</CodeTag> ({info.isDomain ? 'Domain Mode' : 'IP Mode'}).
                    </Typography>
                    <Typography variant="body2" paragraph>
                      3. Set <strong>Server Address</strong> to: <CodeTag>{info.host}</CodeTag>
                    </Typography>
                    <Typography variant="body2" paragraph>
                      4. Set <strong>Server Port</strong> to: <CodeTag>{info.port}</CodeTag> ({info.isHttps ? 'HTTPS Port' : 'HTTP Port'}).
                    </Typography>
                    <Typography variant="body2">
                      5. Save and restart the device. The device will automatically ping your server and stream fingerprint logs live!
                    </Typography>
                  </>
                );
              })()}
            </AccordionDetails>
          </Accordion>

          <Accordion variant="outlined" sx={{ borderRadius: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight={700}>Method 2: eTimeTrackLite Desktop PC Software Setup</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                1. In eTimeTrackLite desktop software, go to <strong>Utilities -&gt; API / Webhook Settings</strong>.
              </Typography>
              <Typography variant="body2" paragraph>
                2. Set Webhook Target URL to: <CodeTag>{webhookUrl}</CodeTag>
              </Typography>
              <Typography variant="body2" paragraph>
                3. Add HTTP Header: <CodeTag>x-api-key: {integrationKey || 'YOUR_INTEGRATION_KEY'}</CodeTag>
              </Typography>
              <Typography variant="body2">
                4. Enable Real-Time Push. Whenever a member touches the fingerprint sensor, attendance will record automatically.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}
